import GameLifeline from "../GameBoard/GameLifeline";
import AvatarOne from "../../assets/images/creategame/avatar-one.png";
import QuestionMediaRenderer from "./Answer/QuestionMediaRenderer";
import React, { useState, useEffect } from "react";
import GreyBtn from "../../components/button/GreyBtn";
import ReportQuestionModal from "../../components/Modal/ReportQuestionModal";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import CountdownTimer from "./Answer/CountdownTimer";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getGameDetailsAPI, useLifelineAPI } from "../../api/game";
import useToast from "../../hooks/useToast";
import Loader from "../../components/Loader/Loader";
import { useTranslation } from "react-i18next";
import PrimaryBtn from "../../components/button/PrimaryBtn";
import GamePlayImg from "../../assets/images/game-board/game-play-2.png";
import { getDefaultPortraitByGender, resolveCharacterUrlFromList } from "../../utils/charcter";
import { baseLanguageCode, normalizeDisplayedLineBreaks } from "../../utils/multilineText";
import { getCharacterListAPI } from "../../api/user";
import { useGameDetailsFromLayout } from "../../layouts/Game/GameDetailsContext";

const GameMysteryQuestion = () => {
  const { t } = useTranslation();
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [selectedParticipantIds, setSelectedParticipantIds] = useState<string[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTimerExpired, setIsTimerExpired] = useState(false);

  const { gameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const currentLanguage = localStorage.getItem("i18nextLng") || "en";
  const baseUrl = import.meta.env.VITE_BASE_CDN_URL;
  const gameDetailsFromLayout = useGameDetailsFromLayout();
  const setIsLifelineUsedOnQuestion = gameDetailsFromLayout?.setIsLifelineUsedOnQuestion;

  useEffect(() => {
    setIsLifelineUsedOnQuestion?.(true);
  }, [setIsLifelineUsedOnQuestion]);

  // Get the mystery question from navigation state
  const questionFromState = location.state?.question;
  const teamIdFromState = location.state?.teamId;
  const teamNameFromState = location.state?.teamName;
  const lifelineIdFromState = location.state?.lifelineId;
  const teamLifelineIdFromState = location.state?.teamLifelineId;

  // Redirect if no question data
  useEffect(() => {
    if (!questionFromState) {
      navigate(`/game/${gameId}`);
    }
  }, [questionFromState, gameId, navigate]);

  // Fetch game data from API for team info
  const { data: gameDetails, isLoading: isLoadingGame } = useQuery({
    queryKey: ["gameDetails", gameId],
    queryFn: () => getGameDetailsAPI(gameId as string),
    enabled: !!gameId,
  });

  const gameData = gameDetails?.data?.game;
  const teamData = gameData?.teams || [];

  // The question data from the API response
  const questionData = questionFromState;
  const questionLang = baseLanguageCode(currentLanguage);
  const mysteryQuestionText = normalizeDisplayedLineBreaks(
    String(questionData?.question?.[questionLang] ?? questionData?.question?.en ?? ""),
  );

  // Submit answer mutation
  const { mutate: submitAnswer } = useMutation({
    mutationFn: useLifelineAPI,
    onSuccess: async (data) => {
      if (isTimerExpired) {
        useToast(t("gameMysteryQuestion.questionMarkedAsTimeout"), "success");
      } else {
        useToast(data?.message || t("gameMysteryQuestion.answerSubmittedSuccessfully"), "success");
      }
      // Await invalidation so fresh data (updated points) is in cache before navigating
      await queryClient.invalidateQueries({ queryKey: ["gameDetails", gameId] });
      // Navigate back to game board
      navigate(`/game/${gameId}`);
    },
    onError: (error: any) => {
      useToast(error.message || t("gameMysteryQuestion.failedToSubmitAnswer"), "error");
      setIsSubmitting(false);
    },
  });

  const [participants, setParticipants] = useState<any[]>([]);

  // Handle answer click - when a team is selected
  const handleAnswerClick = (answer: string, teamId: string) => {
    const team = gameData?.teams?.find((t: any) => t._id === teamId);
    const teamParticipants = team?.participants || [];
    setParticipants(teamParticipants);
    setSelectedAnswer(answer);
    setSelectedTeamId(teamId);

    // Auto-select if only one participant in the team
    if (teamParticipants.length === 1) {
      setSelectedParticipantIds([teamParticipants[0]?.user_id?._id]);
    } else {
      setSelectedParticipantIds([]);
    }

    setShowAnswerModal(true);
  };

  const handleNoneAnswered = () => {
    setSelectedParticipantIds([]);
    setSelectedTeamId(null);
    setShowAnswerModal(false);
    submitAnswer({
      game_id: gameId,
      question_id: questionData?._id,
      user_id: [],
      lifeline_id: lifelineIdFromState,
      team_lifeline_id: teamLifelineIdFromState,
      team_id: teamIdFromState,
      answered_team_id: null,
    });
  };

  // Handle participant selection - toggle multi-select
  const handleParticipantClick = (participantId: string) => {
    setSelectedParticipantIds((prev) =>
      prev.includes(participantId) ? prev.filter((id) => id !== participantId) : [...prev, participantId]
    );
  };

  // Confirm the answer selection and call API
  const handleConfirmAnswer = () => {
    if (!questionData?._id || !selectedTeamId || !gameId) return;

    setIsSubmitting(true);

    // Call API to submit answer with lifeline team + answering team (participants optional)
    submitAnswer({
      game_id: gameId,
      question_id: questionData?._id,
      user_id: selectedParticipantIds,
      lifeline_id: lifelineIdFromState,
      team_lifeline_id: teamLifelineIdFromState,
      team_id: teamIdFromState,
      answered_team_id: selectedTeamId,
    });

    // Close modal and reset states
    setShowAnswerModal(false);
    setSelectedParticipantIds([]);
    setSelectedTeamId(null);
  };

  // Handle timer expiration - just mark expired (no auto-submit)
  const handleTimerExpired = () => {
    if (isTimerExpired) return;
    setIsTimerExpired(true);
  };

  // Show loading state
  if (isLoadingGame || isSubmitting || !questionData) {
    return (
      <div className="flex justify-center items-center h-full min-h-[50vh]">
        <Loader />
      </div>
    );
  }

  const { data: portraitCharacterListData } = useQuery({
    queryKey: ["characterList", "portrait"],
    queryFn: () => getCharacterListAPI({ type: "portrait" }),
    staleTime: 1000 * 60 * 30,
  });
  const portraitCharacters = (portraitCharacterListData as any)?.data?.characters || [];

  return (
    <>
      <div
        className="wrapper flex-1 flex flex-col bg-no-repeat xl:bg-[length:100%_100%] bg-cover bg-bottom"
        style={{
          backgroundImage: `url(${GamePlayImg})`,
          backgroundColor: "#B9F2FD",
        }}
      >
        <section className="w-full flex justify-center items-center relative flex-1">
          <div className="w-full h-full flex justify-center items-center 2xl:pb-[1.9vw] md:pb-6 pb-5 landscape:max-lg:pb-5">
            <div className="w-full 2xl:max-w-[65vw] max-w-335 mx-auto 2xl:px-[0.833vw] xl:px-10 px-4 relative z-20 h-full 2xl:pt-[1vw] xl:pt-3 pt-2 flex flex-col">
              <div className="flex items-center justify-center w-full relative 2xl:mb-[0.5vw] xl:mb-3 mb-2 landscape:max-lg:mb-1">
                <button
                  onClick={() => setShowReportModal(true)}
                  className="landscape:max-lg:col-span-2 col-span-2 cursor-pointer text-light-vermilion font-garamond font-bold 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight inline-flex justify-center items-center 2xl:gap-[0.5vw] md:gap-2 gap-1 2xl:px-[0.83vw] xl:px-4 md:px-3 px-2.5"
                >
                  <span className="2xl:w-[1.2vw] xl:w-5 w-4 2xl:h-[1.2vw] xl:h-5 h-4">
                    <svg
                      className="w-full h-full"
                      width="26"
                      height="24"
                      viewBox="0 0 26 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g filter="url(#filter0_d_3457_147464)">
                        <path
                          d="M14.8728 22.75C17.5036 22.75 19.5804 22.75 21.1186 22.521C22.6805 22.2883 23.9475 21.7836 24.647 20.5488L24.7679 20.3165C25.3177 19.1463 25.0927 17.875 24.5308 16.5019C24.0833 15.4081 23.365 14.1147 22.4573 12.5644L21.4889 10.9272L19.2433 7.14127L19.1897 7.05013C17.9032 4.8811 16.8852 3.16549 15.9381 2.00065C14.9717 0.812175 13.9236 0 12.5418 0C11.1599 4.66667e-05 10.1118 0.812175 9.14544 2.00065C8.42528 2.88643 7.6636 4.09045 6.77451 5.57014L5.84026 7.14127L3.59466 10.9272L3.53884 11.0207C2.19875 13.2799 1.14133 15.0633 0.552674 16.5019C-0.0465261 17.9664 -0.262313 19.3151 0.436462 20.5488L0.574315 20.7722C1.29565 21.8457 2.50076 22.3029 3.96494 22.521C5.50313 22.75 7.57992 22.75 10.2107 22.75H14.8728ZM12.5418 14.2917C11.8975 14.2915 11.3751 13.7692 11.3751 13.125V7.875C11.3751 7.23072 11.8975 6.70841 12.5418 6.70833C13.186 6.70833 13.7085 7.23066 13.7085 7.875V13.125C13.7085 13.7693 13.186 14.2917 12.5418 14.2917ZM12.5418 18.3773C11.8975 18.3772 11.3751 17.8549 11.3751 17.2107V17.1992C11.3751 16.5549 11.8975 16.0327 12.5418 16.0326C13.186 16.0326 13.7085 16.5549 13.7085 17.1992V17.2107C13.7085 17.8549 13.186 18.3773 12.5418 18.3773Z"
                          fill="#FF6C6C"
                        />
                      </g>
                      <defs>
                        <filter
                          id="filter0_d_3457_147464"
                          x="0"
                          y="0"
                          width="25.884"
                          height="23.95"
                          filterUnits="userSpaceOnUse"
                          color-interpolation-filters="sRGB"
                        >
                          <feFlood floodOpacity="0" result="BackgroundImageFix" />
                          <feColorMatrix
                            in="SourceAlpha"
                            type="matrix"
                            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                            result="hardAlpha"
                          />
                          <feOffset dx="0.8" dy="1.2" />
                          <feComposite in2="hardAlpha" operator="out" />
                          <feColorMatrix
                            type="matrix"
                            values="0 0 0 0 0.537255 0 0 0 0 0.0235294 0 0 0 0 0.0235294 0 0 0 1 0"
                          />
                          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3457_147464" />
                          <feBlend
                            mode="normal"
                            in="SourceGraphic"
                            in2="effect1_dropShadow_3457_147464"
                            result="shape"
                          />
                        </filter>
                      </defs>
                    </svg>
                  </span>
                  <span className="underline">{t("gameMysteryQuestion.report")}</span>
                </button>
              </div>
              <div className="flex justify-center 2xl:mb-[1vw] gap-2">
                {/* Mystery Wildcard Badge */}
                <div className="flex justify-center 2xl:mb-[1vw] mb-4">
                  <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-orange flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                    <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-white font-garamond">
                      {t("gameMysteryQuestion.mysteryWildcardQuestion")}
                    </span>
                  </div>
                </div>

                {/* Team Badge */}
                {teamNameFromState && (
                  <div className="flex justify-center 2xl:mb-[1vw] mb-4">
                    <div className="skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                      <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300">
                        {teamNameFromState}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {!showCorrectAnswer && (
                <div className="Ques-part w-full flex-1 2xl:rounded-[1.04vw] rounded-2xl 2xl:border-[0.2vw] xl:border-3 border-2 border-primary bg-primary-yellow 2xl:p-[1.04vw] xl:p-3 p-2 flex 2xl:gap-[1vw] xl:gap-4 md:gap-3 gap-2 overflow-hidden sm:flex-row flex-col flex-1">
                  <div className="bg-white 2xl:border-[0.208vw] xl:border-4 border-2 border-primary w-full 2xl:max-w-[calc(100vw-20.625vw)] xl:max-w-[calc(100vw-396px)] lg:max-w-[calc(100vw-288px)] md:max-w-[calc(100vw-256px)] max-w-full 2xl:rounded-[1.04vw] rounded-2xl 2xl:p-[1vw] p-3 landscape:max-lg:p-2 overflow-hidden sm:h-full flex-1">
                    <div className="w-full mx-auto flex flex-col items-center justify-center 2xl:min-h-[10vw] xl:min-h-[calc(100%-94px)] min-h-[calc(100%-32px)] h-full">
                      <h2
                        dir={questionLang === "ar" ? "rtl" : "ltr"}
                        className="2xl:text-[1.5625vw]/[1.5625vw] xl:text-2xl/6 md:text-xl/5 text-lg/5 font-garamond font-bold text-center whitespace-pre-line wrap-break-word [unicode-bidi:plaintext]"
                      >
                        {mysteryQuestionText}
                      </h2>
                      {/* Media content */}
                      <QuestionMediaRenderer
                        media={questionData?.media}
                        mediaType={questionData?.question_type}
                      />
                    </div>
                  </div>
                  {/* Options Panel */}
                  {questionData?.options && questionData.options.length > 0 ? (
                    <div className="w-full sm:h-full h-fit justify-center items-center 2xl:max-w-[20.625vw] xl:max-w-[396px] lg:max-w-72 md:max-w-64 sm:max-w-60 max-w-full flex flex-col 2xl:gap-[0.625vw] gap-2">
                      {questionData.options.map((option: any, index: number) => (
                        <button
                          key={option._id || index}
                          className="w-full skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 landscape:max-lg:py-0.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300 cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                        >
                          <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1">
                            {typeof option === "object" ? option[currentLanguage] || option.en : option}
                          </span>
                        </button>
                      ))}
                      <button
                        onClick={() => setShowCorrectAnswer(true)}
                        className="w-full skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-orange flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-white cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                      >
                        <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1">
                          {t("gameMysteryQuestion.viewCorrectAnswer")}
                        </span>
                      </button>
                      <button
                        onClick={() => setShowReportModal(true)}
                        className="cursor-pointer text-light-vermilion font-garamond font-bold 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight inline-flex justify-center items-center 2xl:gap-[0.6vw] xl:gap-3 md:gap-2.5 gap-2 2xl:px-[0.83vw] xl:px-4 md:px-3 px-2.5"
                      >
                        <span>
                          <svg
                            className="2xl:w-[1.458vw] 2xl:h-[1.458vw] xl:w-7 w-5 xl:h-7 h-5"
                            width="28"
                            height="28"
                            viewBox="0 0 28 28"
                            fill="none"
                          >
                            <path
                              d="M16.3318 25.375C18.9626 25.375 21.0394 25.375 22.5775 25.146C24.1395 24.9133 25.4065 24.4086 26.106 23.1738L26.2269 22.9415C26.7767 21.7713 26.5517 20.5 25.9898 19.1269C25.5423 18.0331 24.824 16.7397 23.9163 15.1894L22.9478 13.5522L20.7022 9.76627L20.6487 9.67513C19.3622 7.5061 18.3442 5.79049 17.3971 4.62565C16.4307 3.43717 15.3826 2.625 14.0008 2.625C12.6189 2.62505 11.5708 3.43717 10.6044 4.62565C9.88427 5.51143 9.12259 6.71545 8.23349 8.19514L7.29925 9.76627L5.05365 13.5522L4.99782 13.6457C3.65773 15.9049 2.60031 17.6883 2.01166 19.1269C1.41246 20.5914 1.19667 21.9401 1.89545 23.1738L2.0333 23.3972C2.75464 24.4707 3.95975 24.9279 5.42392 25.146C6.96212 25.375 9.0389 25.375 11.6697 25.375H16.3318ZM14.0008 16.9167C13.3564 16.9165 12.8341 16.3942 12.8341 15.75V10.5C12.8341 9.85572 13.3564 9.33341 14.0008 9.33333C14.645 9.33333 15.1675 9.85566 15.1675 10.5V15.75C15.1675 16.3943 14.645 16.9167 14.0008 16.9167ZM14.0008 21.0023C13.3564 21.0022 12.8341 20.4799 12.8341 19.8357V19.8242C12.8341 19.1799 13.3564 18.6577 14.0008 18.6576C14.645 18.6576 15.1675 19.1799 15.1675 19.8242V19.8357C15.1675 20.4799 14.645 21.0023 14.0008 21.0023Z"
                              fill="#FF6C6C"
                            />
                          </svg>
                        </span>
                        <span className="underline">{t("gameMysteryQuestion.report")}</span>
                      </button>
                      {/* Points Badge */}
                      <div className="flex justify-center  2xl:gap-[0.625vw] md:gap-3 gap-2 w-full mx-auto">
                        <button className="relative 2xl:h-[3.229vw] xl:h-8 md:h-8 h-6 landscape:max-lg:h-6 w-fit flex items-center justify-center">
                          <svg
                            width="154"
                            height="62"
                            viewBox="0 0 154 62"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-fit h-full"
                          >
                            <path
                              d="M1.29793 50.0265L3.66775 58.9508C3.80919 59.4835 4.29132 59.8543 4.84242 59.8543H13.1605H20.7805L28.4005 60.5996H35.4762L44.7291 59.8543L55.6148 59.4816L67.0448 59.8543H79.0191L88.8162 60.5996H98.4956C98.5739 60.5996 98.6521 60.592 98.7289 60.577L102.308 59.8769C102.385 59.8618 102.463 59.8543 102.541 59.8543H108.955L112.765 59.4816H122.018L125.283 59.8543H136.511C136.645 59.8543 136.778 59.8764 136.905 59.9198L138.66 60.5208C138.812 60.5728 138.973 60.5942 139.133 60.5838L149.184 59.9284C149.823 59.8867 150.32 59.3561 150.32 58.7156V54.3296C150.32 54.1635 150.286 53.9993 150.221 53.8469L148.336 49.4914C148.212 49.2059 148.203 48.884 148.309 48.5918L150.247 43.286C150.296 43.1524 150.32 43.0113 150.32 42.8691V40.4269C150.32 40.0168 150.598 39.6587 150.995 39.5567C151.664 39.385 151.897 38.5545 151.416 38.0598L148.27 34.8289C148.186 34.7428 148.114 34.6459 148.066 34.5359C147.575 33.4226 147.421 31.3086 150.32 30.5996C152.791 29.9954 151.137 29.7585 149.358 29.6905C148.688 29.6648 148.143 29.1238 148.143 28.4525V27.2019C148.143 26.9864 148.086 26.7749 147.977 26.5888L147.034 24.9736C146.802 24.5767 147.088 24.0779 147.548 24.0779C147.877 24.0779 148.143 23.8113 148.143 23.4824V21.466C148.143 21.0103 148.398 20.593 148.804 20.3848L149.153 20.2055C149.783 19.882 150.004 19.0908 149.633 18.4876L149.282 17.9175C148.784 17.1077 149.367 16.0654 150.318 16.0654H151.282C151.954 16.0654 152.498 15.5213 152.498 14.8501V11.4181C152.498 10.8633 152.122 10.3789 151.585 10.2409L151.234 10.1508C150.696 10.0129 150.32 9.52846 150.32 8.97363V7.31875C150.32 6.76393 149.945 6.27953 149.407 6.14155L149.056 6.05146C148.519 5.91347 148.143 5.42908 148.143 4.87425V3.04813C148.143 2.40536 147.643 1.87372 147.001 1.83496L138.891 1.34495L125.283 0.599609H109.189C109.034 0.599609 108.881 0.629033 108.738 0.686307L105.885 1.82565C105.755 1.87747 105.618 1.90653 105.478 1.91156L95.3476 2.27663L83.9176 1.90396L74.1205 1.34495H47.9948L37.1091 2.27663H20.7805L1.91466 0.746937C1.05025 0.676848 0.392691 1.50865 0.660413 2.33354L1.05843 3.55988C1.14129 3.81519 1.30634 4.03589 1.52781 4.18753L3.37891 5.45497C3.70982 5.68154 3.90766 6.05677 3.90766 6.45781V12.1524V19.4094C3.90766 19.5395 3.88676 19.6688 3.84577 19.7923L1.35545 27.2948C1.24766 27.6195 1.28174 27.9746 1.4493 28.2729L3.60076 32.103C3.79331 32.4457 3.80815 32.8605 3.64058 33.2162L1.44321 37.8802C1.2813 38.2239 1.2894 38.6235 1.46509 38.9603L3.63303 43.1166C3.80632 43.4488 3.81668 43.8424 3.6611 44.1833L1.36693 49.2099C1.25009 49.4659 1.22571 49.7545 1.29793 50.0265Z"
                              fill="#97A4F2"
                              stroke="#110706"
                              strokeWidth="1.2"
                            />
                          </svg>
                          <span className="font-bold 2xl:text-[1.89vw] xl:text-xl/tight lg:text-base/tight text-sm/tight pt-1 absolute inset-0 text-white flex items-center justify-center">
                            {questionData?.difficulty === "easy"
                              ? "200"
                              : questionData?.difficulty === "medium"
                                ? "400"
                                : "600"}
                          </span>
                        </button>
                        {questionData?.time_limit && (
                          <CountdownTimer
                            initialTime={questionData.time_limit}
                            onTimeUp={handleTimerExpired}
                            isStopped={showCorrectAnswer || showReportModal || showAnswerModal || isTimerExpired}
                            disablePause
                            onResetClick={() => setIsTimerExpired(false)}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    /* No options - Show View Answer and Report below media */
                    <div className="w-full 2xl:max-w-[20.625vw] xl:max-w-[396px] lg:max-w-72 md:max-w-64 sm:max-w-60 max-w-full grid sm:grid-cols-1 grid-cols-2 place-content-center 2xl:gap-[0.625vw] gap-2 landscape:max-lg:gap-1.5 justify-center sm:h-full h-fit">
                      <button
                        onClick={() => setShowCorrectAnswer(true)}
                        className="landscape:max-lg:col-span-2 col-span-2 skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-primary-orange flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 landscape:max-lg:py-0.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-white cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                      >
                        <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1">
                          {t("gameMysteryQuestion.viewCorrectAnswer")}
                        </span>
                      </button>

                      {/* Points Badge */}
                      <div className="flex justify-center  2xl:gap-[0.625vw] md:gap-3 gap-2 w-full mx-auto col-span-2">
                        <button className="relative 2xl:h-[3.229vw] xl:h-8 md:h-8 h-6 landscape:max-lg:h-6 w-fit flex items-center justify-center">
                          <svg
                            width="154"
                            height="62"
                            viewBox="0 0 154 62"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-fit h-full"
                          >
                            <path
                              d="M1.29793 50.0265L3.66775 58.9508C3.80919 59.4835 4.29132 59.8543 4.84242 59.8543H13.1605H20.7805L28.4005 60.5996H35.4762L44.7291 59.8543L55.6148 59.4816L67.0448 59.8543H79.0191L88.8162 60.5996H98.4956C98.5739 60.5996 98.6521 60.592 98.7289 60.577L102.308 59.8769C102.385 59.8618 102.463 59.8543 102.541 59.8543H108.955L112.765 59.4816H122.018L125.283 59.8543H136.511C136.645 59.8543 136.778 59.8764 136.905 59.9198L138.66 60.5208C138.812 60.5728 138.973 60.5942 139.133 60.5838L149.184 59.9284C149.823 59.8867 150.32 59.3561 150.32 58.7156V54.3296C150.32 54.1635 150.286 53.9993 150.221 53.8469L148.336 49.4914C148.212 49.2059 148.203 48.884 148.309 48.5918L150.247 43.286C150.296 43.1524 150.32 43.0113 150.32 42.8691V40.4269C150.32 40.0168 150.598 39.6587 150.995 39.5567C151.664 39.385 151.897 38.5545 151.416 38.0598L148.27 34.8289C148.186 34.7428 148.114 34.6459 148.066 34.5359C147.575 33.4226 147.421 31.3086 150.32 30.5996C152.791 29.9954 151.137 29.7585 149.358 29.6905C148.688 29.6648 148.143 29.1238 148.143 28.4525V27.2019C148.143 26.9864 148.086 26.7749 147.977 26.5888L147.034 24.9736C146.802 24.5767 147.088 24.0779 147.548 24.0779C147.877 24.0779 148.143 23.8113 148.143 23.4824V21.466C148.143 21.0103 148.398 20.593 148.804 20.3848L149.153 20.2055C149.783 19.882 150.004 19.0908 149.633 18.4876L149.282 17.9175C148.784 17.1077 149.367 16.0654 150.318 16.0654H151.282C151.954 16.0654 152.498 15.5213 152.498 14.8501V11.4181C152.498 10.8633 152.122 10.3789 151.585 10.2409L151.234 10.1508C150.696 10.0129 150.32 9.52846 150.32 8.97363V7.31875C150.32 6.76393 149.945 6.27953 149.407 6.14155L149.056 6.05146C148.519 5.91347 148.143 5.42908 148.143 4.87425V3.04813C148.143 2.40536 147.643 1.87372 147.001 1.83496L138.891 1.34495L125.283 0.599609H109.189C109.034 0.599609 108.881 0.629033 108.738 0.686307L105.885 1.82565C105.755 1.87747 105.618 1.90653 105.478 1.91156L95.3476 2.27663L83.9176 1.90396L74.1205 1.34495H47.9948L37.1091 2.27663H20.7805L1.91466 0.746937C1.05025 0.676848 0.392691 1.50865 0.660413 2.33354L1.05843 3.55988C1.14129 3.81519 1.30634 4.03589 1.52781 4.18753L3.37891 5.45497C3.70982 5.68154 3.90766 6.05677 3.90766 6.45781V12.1524V19.4094C3.90766 19.5395 3.88676 19.6688 3.84577 19.7923L1.35545 27.2948C1.24766 27.6195 1.28174 27.9746 1.4493 28.2729L3.60076 32.103C3.79331 32.4457 3.80815 32.8605 3.64058 33.2162L1.44321 37.8802C1.2813 38.2239 1.2894 38.6235 1.46509 38.9603L3.63303 43.1166C3.80632 43.4488 3.81668 43.8424 3.6611 44.1833L1.36693 49.2099C1.25009 49.4659 1.22571 49.7545 1.29793 50.0265Z"
                              fill="#97A4F2"
                              stroke="#110706"
                              strokeWidth="1.2"
                            />
                          </svg>
                          <span className="font-bold 2xl:text-[1.89vw] xl:text-xl/tight lg:text-base/tight text-sm/tight pt-1 absolute inset-0 text-white flex items-center justify-center">
                            {questionData?.difficulty === "easy"
                              ? "200"
                              : questionData?.difficulty === "medium"
                                ? "400"
                                : "600"}
                          </span>
                        </button>
                        {questionData?.time_limit && (
                          <CountdownTimer
                            initialTime={questionData.time_limit}
                            onTimeUp={handleTimerExpired}
                            isStopped={showCorrectAnswer || showReportModal || showAnswerModal || isTimerExpired}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Correct Answer Content */}
              {showCorrectAnswer && (
                <div className="Ques-part w-full flex-1 2xl:rounded-[1.04vw] rounded-2xl 2xl:border-[0.2vw] xl:border-3 border-2 border-primary bg-primary-yellow 2xl:p-[1.04vw] xl:p-3 p-2 flex 2xl:gap-[1vw] xl:gap-4 md:gap-3 gap-2 overflow-hidden sm:flex-row flex-col">
                  <div className="bg-white 2xl:border-[0.208vw] xl:border-4 border-2 border-primary w-full 2xl:max-w-[calc(100vw-20.625vw)] xl:max-w-[calc(100vw-396px)] lg:max-w-[calc(100vw-288px)] md:max-w-[calc(100vw-256px)] max-w-full 2xl:rounded-[1.04vw] rounded-2xl 2xl:p-[1vw] p-3 landscape:max-lg:p-2 overflow-hidden sm:h-full flex-1">
                    <div className="relative w-full mx-auto flex flex-col items-center justify-center 2xl:min-h-[calc(100%-2.126vw)] xl:min-h-[calc(100%-60px)] sm:min-h-[calc(100%-32px)] h-full">
                      <h2
                        dir={questionLang === "ar" ? "rtl" : "ltr"}
                        className="2xl:text-[1.5625vw]/[1.5625vw] xl:text-2xl/6 md:text-xl/5 text-lg/5 font-garamond font-bold text-center whitespace-pre-line wrap-break-word [unicode-bidi:plaintext]"
                      >
                        {mysteryQuestionText}
                      </h2>
                      <div className="w-full mx-auto flex flex-col items-center justify-center h-full">
                        {questionData?.answer?.[currentLanguage] && (
                          <p className="text-[#7364FF] font-bold text-center 2xl:text-[3vw]/[3vw] xl:text-4xl/tight text-3xl/7 flex-1 h-full flex items-center justify-center sm:my-0 my-6">
                            {questionData?.answer?.[currentLanguage]}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => setShowCorrectAnswer(false)}
                        className="flex items-center w-full 2xl:gap-[0.417vw] gap-2 cursor-pointer 2xl:mt-[0.7vw] md:mt-3 mt-2 landscape:max-lg:mt-2"
                      >
                        <svg
                          className="2xl:w-[1.042vw] lg:w-5 2xl:h-[1.042vw] lg:h-5 w-4 h-4 rtl:-scale-x-100"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M19.7291 5.74392V13.532C19.7291 14.3452 19.0662 15.0065 18.2533 15.0065H10.6629C10.286 15.0065 9.98042 15.3131 9.98042 15.6898V18.2286C9.98042 18.6727 9.73179 19.0377 9.31756 19.1986C8.90398 19.3594 8.47357 19.2593 8.17329 18.9326L0.276258 10.3409C-0.092086 9.94012 -0.092086 9.33351 0.276258 8.93278L8.17329 0.341062C8.37026 0.126797 8.63346 0 8.92559 0C9.06007 0 9.1924 0.0286874 9.31751 0.0772499C9.7325 0.238219 9.98037 0.602437 9.98037 1.04733V3.58387C9.98037 3.96056 10.286 4.26722 10.6629 4.26722H18.2532C19.0675 4.26722 19.7291 4.92984 19.7291 5.74392Z"
                            fill="#413938"
                          />
                        </svg>
                        <span className="font-garamond font-bold 2xl:text-[1.25vw] xl:text-2xl text-lg leading-none text-center flex items-center mt-1">
                          {t("gameAnswer.backToTheQuestion")}
                        </span>
                      </button>
                    </div>
                  </div>
                  <div className="w-full 2xl:max-w-[20.625vw] xl:max-w-[396px] lg:max-w-72 md:max-w-64 sm:max-w-60 max-w-full grid sm:grid-cols-1 grid-cols-2 place-content-center 2xl:gap-[0.625vw] gap-2 landscape:max-lg:gap-1.5 justify-center sm:h-full h-fit">
                    <p className="col-span-2 2xl:text-[1.25vw]/tight xl:text-xl/tight text-lg/tight landscape:max-lg:text-base/tight text-black-300 font-bold text-center sm:mb-0 -mb-1">
                      {t("gameMysteryQuestion.whoMadeTheCorrectAnswer")}
                    </p>
                    {gameData?.teams?.map((team: any) => (
                      <button
                        onClick={() => handleAnswerClick(team.team_name, team._id)}
                        key={team.team_name}
                        className="sm:col-span-2 landscape:max-lg:col-span-2 skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1 lg:py-0.5 sm:py-1 py-0.5 landscape:max-lg:py-0.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300 cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                      >
                        <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1">{team.team_name}</span>
                      </button>
                    ))}
                    <button
                      onClick={() => handleNoneAnswered()}
                      key="none"
                      className="sm:col-span-2 landscape:max-lg:col-span-2 skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[#E7E6E6] flex items-center 2xl:py-[0.5vw] xl:py-1 lg:py-0.5 sm:py-1 py-0.5 landscape:max-lg:py-0.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold text-black-300 cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
                    >
                      <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1">{t("gameMysteryQuestion.none")}</span>
                    </button>

                    {/* Points Badge */}
                    <div className="flex justify-center  2xl:gap-[0.625vw] md:gap-3 gap-2 w-full mx-auto col-span-2">
                      <button className="relative 2xl:h-[3.229vw] xl:h-8 md:h-8 h-6 landscape:max-lg:h-6 w-fit flex items-center justify-center">
                        <svg
                          width="154"
                          height="62"
                          viewBox="0 0 154 62"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="w-fit h-full"
                        >
                          <path
                            d="M1.29793 50.0265L3.66775 58.9508C3.80919 59.4835 4.29132 59.8543 4.84242 59.8543H13.1605H20.7805L28.4005 60.5996H35.4762L44.7291 59.8543L55.6148 59.4816L67.0448 59.8543H79.0191L88.8162 60.5996H98.4956C98.5739 60.5996 98.6521 60.592 98.7289 60.577L102.308 59.8769C102.385 59.8618 102.463 59.8543 102.541 59.8543H108.955L112.765 59.4816H122.018L125.283 59.8543H136.511C136.645 59.8543 136.778 59.8764 136.905 59.9198L138.66 60.5208C138.812 60.5728 138.973 60.5942 139.133 60.5838L149.184 59.9284C149.823 59.8867 150.32 59.3561 150.32 58.7156V54.3296C150.32 54.1635 150.286 53.9993 150.221 53.8469L148.336 49.4914C148.212 49.2059 148.203 48.884 148.309 48.5918L150.247 43.286C150.296 43.1524 150.32 43.0113 150.32 42.8691V40.4269C150.32 40.0168 150.598 39.6587 150.995 39.5567C151.664 39.385 151.897 38.5545 151.416 38.0598L148.27 34.8289C148.186 34.7428 148.114 34.6459 148.066 34.5359C147.575 33.4226 147.421 31.3086 150.32 30.5996C152.791 29.9954 151.137 29.7585 149.358 29.6905C148.688 29.6648 148.143 29.1238 148.143 28.4525V27.2019C148.143 26.9864 148.086 26.7749 147.977 26.5888L147.034 24.9736C146.802 24.5767 147.088 24.0779 147.548 24.0779C147.877 24.0779 148.143 23.8113 148.143 23.4824V21.466C148.143 21.0103 148.398 20.593 148.804 20.3848L149.153 20.2055C149.783 19.882 150.004 19.0908 149.633 18.4876L149.282 17.9175C148.784 17.1077 149.367 16.0654 150.318 16.0654H151.282C151.954 16.0654 152.498 15.5213 152.498 14.8501V11.4181C152.498 10.8633 152.122 10.3789 151.585 10.2409L151.234 10.1508C150.696 10.0129 150.32 9.52846 150.32 8.97363V7.31875C150.32 6.76393 149.945 6.27953 149.407 6.14155L149.056 6.05146C148.519 5.91347 148.143 5.42908 148.143 4.87425V3.04813C148.143 2.40536 147.643 1.87372 147.001 1.83496L138.891 1.34495L125.283 0.599609H109.189C109.034 0.599609 108.881 0.629033 108.738 0.686307L105.885 1.82565C105.755 1.87747 105.618 1.90653 105.478 1.91156L95.3476 2.27663L83.9176 1.90396L74.1205 1.34495H47.9948L37.1091 2.27663H20.7805L1.91466 0.746937C1.05025 0.676848 0.392691 1.50865 0.660413 2.33354L1.05843 3.55988C1.14129 3.81519 1.30634 4.03589 1.52781 4.18753L3.37891 5.45497C3.70982 5.68154 3.90766 6.05677 3.90766 6.45781V12.1524V19.4094C3.90766 19.5395 3.88676 19.6688 3.84577 19.7923L1.35545 27.2948C1.24766 27.6195 1.28174 27.9746 1.4493 28.2729L3.60076 32.103C3.79331 32.4457 3.80815 32.8605 3.64058 33.2162L1.44321 37.8802C1.2813 38.2239 1.2894 38.6235 1.46509 38.9603L3.63303 43.1166C3.80632 43.4488 3.81668 43.8424 3.6611 44.1833L1.36693 49.2099C1.25009 49.4659 1.22571 49.7545 1.29793 50.0265Z"
                            fill="#97A4F2"
                            stroke="#110706"
                            strokeWidth="1.2"
                          />
                        </svg>
                        <span className="font-bold 2xl:text-[1.89vw] xl:text-xl/tight lg:text-base/tight text-sm/tight pt-1 absolute inset-0 text-white flex items-center justify-center">
                          {questionData?.difficulty === "easy"
                            ? "200"
                            : questionData?.difficulty === "medium"
                              ? "400"
                              : "600"}
                        </span>
                      </button>
                      {questionData?.time_limit && (
                        <CountdownTimer
                          initialTime={questionData.time_limit}
                          onTimeUp={handleTimerExpired}
                          isStopped={showCorrectAnswer || showReportModal || showAnswerModal || isTimerExpired}
                          disablePause
                          onResetClick={() => setIsTimerExpired(false)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Teams Section */}
        <section className="game-bottom-banner 2xl:py-[1.5vw] xl:py-5 py-3 landscape:max-lg:py-3 relative">
          <div className="w-full 2xl:max-w-[65vw] max-w-335 mx-auto 2xl:px-[0.833vw] xl:px-10 px-4 relative z-20">
            <div className="flex flex-wrap justify-center 2xl:gap-[1.25vw] xl:gap-5 md:gap-3 gap-2.5">
              {teamData?.map((team: any) => (
                <div
                  key={team._id}
                  className="w-[calc(50%-0.375rem)] xs:w-[calc(33.33%-7px)] sm:w-[calc(25%-1rem)] xl:w-[calc(25%-3rem)] 2xl:w-[calc(25%-3vw)]"
                >
                  <GameLifeline
                    team={team}
                    score={team.total_points}
                    lifelines={team.lifelines}
                    isCurrentTurn={team?._id == teamIdFromState}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Answer Modal */}
        {showAnswerModal && (
          <>
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50">
              <div className="modal bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[40px] md:rounded-4xl rounded-3xl 2xl:p-[2vw] xl:p-10 md:p-7 p-4 2xl:max-w-[28.646vw] max-w-[400px] w-full relative animate-[slideUp_0.3s_ease-out] lg:border-[0.2vw] border-3 border-primary overflow-auto lg:max-h-[calc(100dvh-11%)] max-h-[calc(100dvh-40px)]">
                <div className="flex lg:gap-[1vw] gap-2 items-start justify-between w-full 2xl:mb-[1vw] md:mb-4 mb-3">
                  <h1 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/6 text-2xl/5 font-semibold text-primary-orange">
                    {t("gameMysteryQuestion.selectParticipant")}
                  </h1>
                  <button
                    onClick={() => {
                      setShowAnswerModal(false);
                      setSelectedParticipantIds([]);
                    }}
                    className="lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 md:h-5 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300"
                  >
                    <svg className="w-full h-full" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                        fill="#333938"
                      />
                    </svg>
                  </button>
                </div>

                <div className="w-full 2xl:mb-[1.042vw] mb-5">
                  <svg className="w-full" width="540" height="1" viewBox="0 0 540 1" fill="none">
                    <line
                      y1="-0.5"
                      x2="540"
                      y2="-0.5"
                      transform="matrix(-1 0 0 1 540 1)"
                      stroke="url(#paint0_linear_mystery)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_mystery"
                        x1="0"
                        y1="0.5"
                        x2="540"
                        y2="0.5"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#0B3369" stopOpacity="0" />
                        <stop offset="0.5" stopColor="#7364FF" />
                        <stop offset="1" stopColor="#0B3369" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>

                {/* Participant List */}
                <div className="2xl:space-y-[0.833vw] md:space-y-4 space-y-3 border-2 border-black-300 bg-primary-ivory 2xl:p-[1vw] p-4 2xl:rounded-[0.833vw] rounded-xl">
                  {participants.map((participant) => {
                    const participantId = participant?.user_id?._id;
                    const isSelected = selectedParticipantIds.includes(participantId);
                    const participantPortrait =
                      resolveCharacterUrlFromList(participant?.user_id?.profile_image, portraitCharacters) ||
                      getDefaultPortraitByGender(
                        participant?.user_id?.gender || participant?.gender,
                        portraitCharacters
                      ) ||
                      null;
                    return (
                      <button
                        key={participant._id}
                        onClick={() => handleParticipantClick(participantId)}
                        className={`cursor-pointer w-full flex items-center 2xl:gap-[0.6vw] gap-2`}
                      >
                        {/* <div
                          className={`2xl:w-[1.2vw] 2xl:h-[1.2vw] w-5 h-5 rounded-md flex items-center justify-center transition-all ${
                            isSelected ? "bg-primary" : "bg-white border-2 border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div> */}
                        <div
                          className="2xl:w-[2.5vw] xl:w-[40px] lg:w-10 w-10 2xl:min-w-[2.5vw] xl:min-w-[40px] lg:min-w-10 min-w-10 2xl:h-[2.5vw] xl:h-[40px] lg:h-10 h-10 aspect-square overflow-hidden
                              bg-primary-light-orange skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] flex justify-center items-center"
                        >
                          {participantPortrait ? (
                            <img
                              src={participantPortrait}
                              alt={participant?.user_id?.full_name}
                              className="w-full h-full object-cover"
                            />
                          ) : <span className="text-center text-xl">
                            {participant?.user_id?.full_name?.charAt(0)?.toUpperCase() || "?"}
                          </span>}
                        </div>
                        <div
                          className={`flex-1 skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 flex items-center 2xl:py-[0.54vw] py-1.5 2xl:px-[1.042vw] lg:px-4 md:px-3  px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:text-[0.9375vw]/[1.04vw] text-base/tight font-bold cursor-pointer justify-center text-nowrap 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]
                        ${isSelected ? "bg-primary-orange text-white" : "bg-[#F2BF7D] text-black-300"}`}
                        >
                          <span className={`skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 text-start w-full`}>
                            {participant.full_name}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {selectedParticipantIds.length > 0 && (
                  <p className="2xl:text-[0.75vw] text-sm text-primary-orange font-bold 2xl:mt-[0.8vw] mt-3 text-center">
                    {selectedParticipantIds.length}{" "}
                    {selectedParticipantIds.length > 1
                      ? t("gameAnswer.participantsSelected")
                      : t("gameAnswer.participantSelected")}
                  </p>
                )}

                <PrimaryBtn
                  onClick={handleConfirmAnswer}
                  className="!mb-0 w-full min-w-full 2xl:mt-[1vw] mt-4"
                >
                  {t("gameAnswer.confirmAnswer")}
                </PrimaryBtn>
              </div>
            </div>
            <style>{`
              @keyframes slideUp {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
              }
            `}</style>
          </>
        )}

        {showReportModal && (
          <ReportQuestionModal
            isOpen={showReportModal}
            onClose={() => setShowReportModal(false)}
            questionData={questionData}
            quistionIdToReport={questionData?._id}
          />
        )}
      </div>
    </>
  );
};

export default GameMysteryQuestion;
